import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Clock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

/* ===========================================================
   ABSOLUTE API URL (no envs, no rewrites required)
   =========================================================== */
const API = 'https://vashakaur.com/admin/api.php?path=';

// Build endpoint safely for api.php?path= (ALWAYS includes the leading slash)
const apiUrl = (p) => `${API}${p.startsWith('/') ? p : `/${p}`}`;

/* Optional: axios instance (cleaner calls) */
const http = axios.create({
  baseURL: API, // e.g., https://.../api.php?path=
  headers: { 'Content-Type': 'application/json' },
});

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // One-time health probe (remove after debugging)
  useEffect(() => {
    let done = false; // avoids duplicate logs under StrictMode
    const u = apiUrl('/health');
    if (!done) {
      console.log('Health GET →', u);
      fetch(u)
        .then(async r => {
          const t = await r.text();
          console.log('Health raw response:', t);
        })
        .catch(e => console.error('Health failed', e));
    }
    done = true;
  }, []);

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const url = apiUrl('/appointments');
      console.log('POST →', url);

      // Use axios instance for consistency
      const res = await http.post('/appointments', formData, {
        // when baseURL ends with ?path=, axios will NOT auto-join; so send full URL:
        // to keep it simple and explicit, just pass the fully built URL:
      });

      // If your axios instance above doesn't join correctly with ?path=, use:
      // const res = await axios.post(url, formData, { headers: { 'Content-Type': 'application/json' } });

      if ((res.status === 200 || res.status === 201) && res.data?.ok !== false) {
        toast.success('Appointment request submitted successfully!');
        reset();
      } else {
        const msg = res.data?.error || 'Unexpected response from server.';
        toast.error(msg);
      }
    } catch (err) {
      const raw = err?.response?.data;
      const msg =
        (typeof raw === 'string' && raw.slice(0, 200)) ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to submit appointment request.';
      toast.error(msg);
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-large bg-gradient-to-br from-blue-50 to-white" data-testid="contact-hero">
        <div className="container text-center">
          <h1 className="heading-primary" data-testid="contact-title">Contact Us</h1>
          <p className="text-large max-w-3xl mx-auto" data-testid="contact-subtitle">
            Schedule your consultation with Professor Vasha Kaur or get in touch
            with any questions about our services.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section" data-testid="contact-main">
        <div className="container">
          <div className="grid grid-2 gap-12">

            {/* Appointment Booking Form */}
            <div data-testid="appointment-form-section">
              <div className="mb-8">
                <h2 className="heading-secondary" data-testid="form-title">Book Your Appointment</h2>
                <p className="text-base" data-testid="form-description">
                  Complete the form below to request an appointment. We will contact you within
                  24 hours to confirm your preferred date and time.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="appointment-form">
                <div className="grid grid-2 gap-4">
                  <div className="form-group">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      {...register('first_name', { required: 'First name is required' })}
                      data-testid="first-name-input"
                      disabled={isSubmitting}
                    />
                    {errors.first_name && (
                      <p className="form-error" data-testid="first-name-error">{errors.first_name.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      {...register('last_name', { required: 'Last name is required' })}
                      data-testid="last-name-input"
                      disabled={isSubmitting}
                    />
                    {errors.last_name && (
                      <p className="form-error" data-testid="last-name-error">{errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    data-testid="email-input"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="form-error" data-testid="email-error">{errors.email.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    {...register('phone', { required: 'Phone number is required' })}
                    data-testid="phone-input"
                    disabled={isSubmitting}
                  />
                  {errors.phone && (
                    <p className="form-error" data-testid="phone-error">{errors.phone.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    {...register('date_of_birth', { required: 'Date of birth is required' })}
                    data-testid="dob-input"
                    disabled={isSubmitting}
                  />
                  {errors.date_of_birth && (
                    <p className="form-error" data-testid="dob-error">{errors.date_of_birth.message}</p>
                  )}
                </div>

                <div className="grid grid-2 gap-4">
                  <div className="form-group">
                    <Label htmlFor="preferred_date">Preferred Date *</Label>
                    <Input
                      id="preferred_date"
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      {...register('preferred_date', { required: 'Preferred date is required' })}
                      data-testid="preferred-date-input"
                      disabled={isSubmitting}
                    />
                    {errors.preferred_date && (
                      <p className="form-error" data-testid="preferred-date-error">{errors.preferred_date.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="preferred_time">Preferred Time *</Label>
                    <select
                      id="preferred_time"
                      {...register('preferred_time', { required: 'Preferred time is required' })}
                      className="form-select"
                      data-testid="preferred-time-select"
                      disabled={isSubmitting}
                    >
                      <option value="">Select time</option>
                      <option value="9:00 AM">9:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="2:00 PM">2:00 PM</option>
                      <option value="3:00 PM">3:00 PM</option>
                      <option value="4:00 PM">4:00 PM</option>
                    </select>
                    {errors.preferred_time && (
                      <p className="form-error" data-testid="preferred-time-error">{errors.preferred_time.message}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="reason">Reason for Visit *</Label>
                  <select
                    id="reason"
                    {...register('reason', { required: 'Reason for visit is required' })}
                    className="form-select"
                    data-testid="reason-select"
                    disabled={isSubmitting}
                  >
                    <option value="">Select reason</option>
                    <option value="Abdominal pain">Abdominal pain</option>
                    <option value="Reflux symptoms">Reflux symptoms</option>
                    <option value="Metabolic health / weight management">Metabolic health / weight management</option>
                    <option value="Gallstone management">Gallstone management</option>
                    <option value="Gallbladder surgery">Gallbladder surgery</option>
                    <option value="Hernia surgery">Hernia surgery</option>
                    <option value="Second Opinion">Second Opinion</option>
                    <option value="General Consultation">General Consultation</option>
                    <option value="Other – please specify">Other – please specify</option>
                    <option value="Follow-up Appointment">Follow-up Appointment</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reason && (
                    <p className="form-error" data-testid="reason-error">{errors.reason.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    {...register('notes')}
                    placeholder="Please provide any additional information about your condition or questions you'd like to discuss..."
                    rows={4}
                    data-testid="notes-textarea"
                    disabled={isSubmitting}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                  data-testid="submit-appointment-btn"
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner mr-2"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Appointment Request'
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div data-testid="contact-info">
              <div className="mb-8">
                <h2 className="heading-secondary" data-testid="contact-info-title">Get In Touch</h2>
                <p className="text-base" data-testid="contact-info-description">
                  For immediate assistance or general inquiries, please use the
                  contact information below.
                </p>
              </div>

              <div className="space-y-8">
                {/* Office Hours */}
                <div className="card" data-testid="office-hours">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="heading-tertiary mb-2">Office Hours</h3>
                      <div className="space-y-1 text-gray-600">
                        <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                        <p>Saturday: 9:00 AM - 1:00 PM</p>
                        <p>Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
{/* Contact Details */}
<div className="card" data-testid="contact-details">
  <div className="flex items-start gap-4">
    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
      <Phone size={24} className="text-blue-600" />
    </div>

    <div>
      <h3 className="heading-tertiary mb-2">Phone & Email</h3>

      <div className="space-y-4 text-gray-600">

        <div>
          <p className="font-semibold text-gray-900">Secretary</p>
          <p><strong>Phone:</strong> 0203 198 9826</p>
          <p className="text-sm">
            Monday – Friday, 9:00 AM – 5:00 PM.
          </p>
          <p className="text-sm">
            If we're unavailable, please leave a message and we'll return your call.
          </p>
        </div>

        <div>
          <p className="font-semibold text-gray-900">Email</p>
          <p>
            <strong>Email:</strong> ProfKaur.PA@outlook.com
          </p>
          <p className="text-sm">
            <div className="pt-4">
  <a
    href="https://professor-vasha-kaur.carebit.co/patients/bookings/new?clinician=ms-vasha-kaur"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-white font-semibold transition hover:bg-blue-700"
  >
    Book Appointment Online
  </a>
</div>
          </p>
        </div>

      </div>
    </div>
  </div>
</div>

              {/* Clinic Locations */}
{/* Clinic Locations */}
<div className="card" data-testid="clinic-locations">
  <div className="flex items-start gap-4">
    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
      <MapPin size={24} className="text-blue-600" />
    </div>

    <div>
      <h3 className="heading-tertiary mb-4">
        Clinic Locations
      </h3>

      <div className="space-y-8 text-gray-600">

        {/* New Victoria Hospital */}
        <div>
          <p className="font-semibold text-gray-900 text-lg">
            New Victoria Hospital
          </p>

          <p>184 Coombe Lane West</p>
          <p>Kingston upon Thames</p>
          <p>KT2 7EG</p>

          <p className="mt-2">
            <strong>Telephone:</strong> 0208 949 9000
          </p>

          <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 p-3">
            <p className="font-medium text-gray-900">
              Consultation Availability
            </p>
            <p>Every Tuesday afternoon</p>
          </div>
        </div>

        {/* Kingston Private Health */}
        <div>
          <p className="font-semibold text-gray-900 text-lg">
            Kingston Private Health
          </p>

          <p>7th Floor, Esher Wing</p>
          <p>Kingston Hospital</p>
          <p>Galsworthy Road</p>
          <p>Kingston upon Thames</p>
          <p>KT2 7QB</p>

          <p className="mt-2">
            <strong>Telephone:</strong> 020 8546 6677
          </p>

          <div className="mt-3 rounded-lg bg-green-50 border border-green-100 p-3">
            <p className="font-medium text-gray-900">
              Consultation Availability
            </p>
            <p>Flexible appointments available between 8:00 AM – 7:00 PM</p>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>

                {/* Emergency Contact */}
                <div className="card bg-red-50 border-red-200" data-testid="emergency-contact">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-red-600" />
                    </div>
                    <div>
                      <h3 className="heading-tertiary text-red-800 mb-2">Emergency Contact</h3>
                      <p className="text-red-700">
                        For surgical emergencies outside office hours, please contact
                        your local A&amp;E department or call 999.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>{/* /contact-info */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
